import type { ProductFragment, ProductVariantFragment, SearchResultPriceFragment } from '#graphql-operations'
import type { MaybeRefOrGetter } from 'vue'
import type { FreeShippingThresholds } from '~/composables/useFreeShippingThreshold'
import type { MarketShipping } from '~/constants/marketPolicy'
import { appLocales } from '~/config/i18n'
import {
  HANDLING_DAYS,
  MARKET_SHIPPING,
  marketCountriesForLocale,
  returnsPolicyForLocale,
} from '~/constants/marketPolicy'
import { SUPPORT_PHONE_LOCALES } from '~/constants/supportPhone'

/** Reduce a search-result `SearchResultPriceFragment` to a single integer cents value. */
export function searchResultPrice(price: SearchResultPriceFragment | undefined | null): number | undefined {
  if (!price)
    return undefined
  if ('value' in price)
    return price.value
  if ('min' in price)
    return price.min
  return undefined
}

type Resolvable<T> = T | MaybeRefOrGetter<T> | (() => T)

interface BreadcrumbItem {
  label: string
  href?: string
}

/**
 * One customer review, in the shape `ProductReviewFragment` already delivers.
 * Structural rather than the generated type so the PDP can pass its review
 * items straight through without mapping.
 */
export interface SeoReview {
  authorName?: string | null
  rating?: number | null
  summary?: string | null
  body?: string | null
  createdAt?: string | null
}

export interface SeoMetaInput {
  title?: Resolvable<string | undefined>
  description?: Resolvable<string | undefined>
  /** Path-relative canonical (e.g. `/products/123/slug`). Resolved to absolute. */
  canonical?: Resolvable<string | undefined>
  /** Absolute image URL; falls back to the site OG image. */
  image?: Resolvable<string | undefined>
  /** `product`, `website`, etc. Defaults to `website`. */
  ogType?: Resolvable<string | undefined>
  noindex?: Resolvable<boolean | undefined>
  keywords?: Resolvable<string | undefined>
  /**
   * Restrict hreflang / og:locale:alternate to these locale codes. Defaults to
   * every locale this storefront ships. Pass a subset for pages that only
   * exist in some locales (e.g. the DE-only `/impressum`) so the alternates
   * don't point at URLs that 404.
   */
  hreflangLocales?: Resolvable<string[] | undefined>
  /**
   * Locale-less path per locale code (e.g. `{ de: '/products/4110/sun-glow-…' }`)
   * for pages whose URL is itself translated — a product's slug differs by
   * language. Each listed locale's hreflang points at its own path; unlisted
   * locales reuse the current one. The current locale always uses `canonical`,
   * so the self-referencing alternate can never disagree with it.
   */
  alternatePaths?: Resolvable<Record<string, string> | undefined>
}

function resolve<T>(value: Resolvable<T>): T {
  if (typeof value === 'function')
    return (value as () => T)()
  return unref(value) as T
}

/**
 * Shared SEO helpers for exalted pages. Centralises canonical URL building,
 * Open Graph + Twitter meta, and JSON-LD generation (Organization, WebSite,
 * BreadcrumbList, Product, CollectionPage/ItemList) so each page only has to
 * describe its own data.
 *
 * All SEO inputs accept getters or refs so the meta + canonical + JSON-LD
 * stay in sync as the page data resolves without accumulating duplicate
 * `<head>` entries.
 */
export function useSeo() {
  const config = useRuntimeConfig()
  const route = useRoute()
  const localePath = useLocalePath()
  const { t, locale } = useI18n()

  // Hreflang / og:locale alternates only cover the locales this storefront
  // ships — NOT every locale registered in the shared i18n layer. The layer
  // exports ~25 locales (shared currencies/countries) and @nuxtjs/i18n's
  // `useLocaleHead({ addSeoAttributes })` would otherwise emit alt links for
  // all of them. `appLocales` is the 7-locale exalted whitelist.
  const SHIPPING_LOCALES = appLocales.map(l => ({
    code: l.code,
    // BCP47 tag (e.g. `cs-CZ`) — better for `hreflang` than the bare code.
    language: l.language ?? l.code,
  }))

  const SITE_NAME = 'EXALTED'
  const DEFAULT_OG_IMAGE = '/image.png'
  // Only reached on a misconfigured build: `i18n.baseUrl`
  // (NUXT_PUBLIC_I18N_BASE_URL) is the canonical public origin and is always
  // set. The previous fallback read `public.host`, which resolves to the
  // INTERNAL origin behind the CDN (storefront-prod.*) — wrong in canonicals
  // and infrastructure metadata that no longer ships to the browser.
  const FALLBACK_ORIGIN = 'https://exalted.com'

  function siteOrigin(): string {
    const base = config.public.i18n?.baseUrl
    if (typeof base === 'string' && base)
      return base.replace(/\/$/, '')
    return FALLBACK_ORIGIN
  }

  function absoluteUrl(path: string | undefined): string {
    if (!path)
      return siteOrigin()
    if (path.startsWith('http'))
      return path
    const origin = siteOrigin()
    const p = path.startsWith('/') ? path : `/${path}`
    return `${origin}${p}`
  }

  /**
   * Split a path into `{ localeCode, restPath }`. Detects when the path is
   * already prefixed with a known locale; otherwise treats the whole path
   * as locale-less.
   */
  function splitLocaleFromPath(path: string): { code: string | null, rest: string } {
    const cleanPath = path.startsWith('/') ? path : `/${path}`
    const codes = SHIPPING_LOCALES.map(l => l.code).join('|')
    const match = cleanPath.match(new RegExp(`^/(${codes})(?=/|$)(.*)`))
    if (match)
      return { code: match[1]!, rest: match[2] || '' }
    return { code: null, rest: cleanPath === '/' ? '' : cleanPath }
  }

  /**
   * `?page=N` suffix for paginated listings, empty everywhere else.
   *
   * Paginated pages are indexable (see `hasNoindexQuery`), which only works if
   * each one canonicalises to itself — a canonical pointing back at page 1 would
   * have search engines discard pages 2+ as duplicates. Derived from the route
   * rather than passed in, so it is inert on pages that never carry a `page`.
   */
  function pageSuffix(): string {
    const raw = route.query.page
    const value = Array.isArray(raw) ? raw[0] : raw
    const parsed = Number.parseInt(value ?? '', 10)
    return Number.isFinite(parsed) && parsed > 1 ? `?page=${parsed}` : ''
  }

  /** Absolute, locale-prefixed canonical URL for the current page. */
  function canonicalForPath(path?: string): string {
    const raw = path ?? route.path
    const { rest } = splitLocaleFromPath(raw)
    const currentLocale = unref(locale)
    return absoluteUrl(`/${currentLocale}${rest}${pageSuffix()}`)
  }

  /**
   * The locales a given page is actually published in, defaulting to all of
   * them. An empty/unknown restriction falls back to the full list rather than
   * emitting no alternates at all.
   */
  function localesFor(only?: string[]) {
    if (!only?.length)
      return SHIPPING_LOCALES
    const subset = SHIPPING_LOCALES.filter(l => only.includes(l.code))
    return subset.length ? subset : SHIPPING_LOCALES
  }

  function hreflangLinks(path: string, only?: string[], alternatePaths?: Record<string, string>) {
    const suffix = pageSuffix()
    const { rest } = splitLocaleFromPath(path)
    const locales = localesFor(only)
    const hrefFor = (code: string) => {
      const localeRest = code === unref(locale) ? rest : (alternatePaths?.[code] ?? rest)
      return absoluteUrl(`/${code}${localeRest}${suffix}`)
    }
    const links = locales.map(l => ({
      rel: 'alternate',
      hreflang: l.language,
      href: hrefFor(l.code),
    }))
    // Point `x-default` at the default-locale variant — search engines use
    // this when none of the explicit alternates match the user's language.
    const defaultLocale = locales[0]?.code
    if (defaultLocale) {
      links.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: hrefFor(defaultLocale),
      })
    }
    return links
  }

  /**
   * Apply page-level SEO meta + canonical + hreflang. Inputs may be refs or
   * getters so the head stays reactive without re-registering entries.
   */
  function setSeoMeta(input: SeoMetaInput) {
    const titleGetter = () => resolve(input.title)
    const descriptionGetter = () => resolve(input.description)
    const imageGetter = () => {
      const img = resolve(input.image)
      return img ? absoluteUrl(img) : absoluteUrl(DEFAULT_OG_IMAGE)
    }
    const canonicalGetter = () => canonicalForPath(resolve(input.canonical))
    const ogTypeGetter = (): any => resolve(input.ogType) ?? 'website'
    const robotsGetter = () => (resolve(input.noindex) ? 'noindex, nofollow' : 'index, follow')
    const keywordsGetter = () => resolve(input.keywords)

    // Convert BCP47 (`cs-CZ`) → OG locale format (`cs_CZ`).
    const toOgLocale = (lang: string) => lang.replace('-', '_')
    const currentLanguage = () => SHIPPING_LOCALES.find(l => l.code === unref(locale))?.language ?? unref(locale)

    useSeoMeta({
      title: titleGetter,
      description: descriptionGetter,
      ogTitle: titleGetter,
      ogDescription: descriptionGetter,
      ogType: ogTypeGetter,
      ogUrl: canonicalGetter,
      ogImage: imageGetter,
      ogSiteName: SITE_NAME,
      ogLocale: () => toOgLocale(currentLanguage()),
      // og:locale:alternate is constrained to the 7 exalted-only locales so
      // social-share previews don't list every locale from the shared layer.
      ogLocaleAlternate: () => localesFor(resolve(input.hreflangLocales))
        .filter(l => l.code !== unref(locale))
        .map(l => toOgLocale(l.language)),
      twitterCard: 'summary_large_image',
      twitterTitle: titleGetter,
      twitterDescription: descriptionGetter,
      twitterImage: imageGetter,
      keywords: keywordsGetter,
      robots: robotsGetter,
    })

    useHead({
      link: () => {
        const path = resolve(input.canonical) ?? route.path
        return [
          // Use `tagPriority: 'high'` so this canonical wins over any link
          // emitted earlier by app.vue's `useLocaleHead({ addSeoAttributes: true })`.
          { rel: 'canonical', href: canonicalForPath(path), tagPriority: 'high' },
          ...hreflangLinks(path, resolve(input.hreflangLocales), resolve(input.alternatePaths))
            .map(l => ({ ...l, tagPriority: 'high' as const })),
        ]
      },
    })
  }

  /**
   * Inject reactive JSON-LD into `<head>`. Pass a getter so the script
   * `innerHTML` updates instead of accumulating new entries.
   */
  function addJsonLd(getData: () => Record<string, any> | Record<string, any>[] | null | undefined) {
    useHead({
      script: () => {
        const data = getData()
        if (!data)
          return []
        return [{
          type: 'application/ld+json',
          innerHTML: JSON.stringify(data),
        }]
      },
    })
  }

  function organizationJsonLd() {
    const socials = config.public.socials || {}
    const sameAs = Object.values(socials)
      .map((s: any) => s?.href)
      .filter((href): href is string => typeof href === 'string')

    const company = config.public.company || {}
    // Published only where the contact page shows it; JSON.stringify drops the
    // key elsewhere.
    const telephone = SUPPORT_PHONE_LOCALES.includes(unref(locale)) ? company.phoneNumber : undefined
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteOrigin()}/#organization`,
      // The Organization is the legal entity behind the store, so `name` is the
      // registered company; the storefront brand rides along as alternateName
      // so the two stay associated for search engines.
      'name': company.name || SITE_NAME,
      'alternateName': SITE_NAME,
      'url': siteOrigin(),
      'logo': absoluteUrl('/icon.png'),
      'sameAs': sameAs,
      ...(company.emailAddress || telephone
        ? {
            contactPoint: [{
              '@type': 'ContactPoint',
              'contactType': 'customer support',
              'email': company.emailAddress,
              'telephone': telephone,
              'availableLanguage': SHIPPING_LOCALES.map(l => l.language),
            }],
          }
        : {}),
      ...(company.streetLine1
        ? {
            address: {
              '@type': 'PostalAddress',
              'streetAddress': [company.streetLine1, company.streetLine2].filter(Boolean).join(', '),
              'addressLocality': company.city,
              // Omitted rather than emitted empty: Prague has no region.
              ...(company.province ? { addressRegion: company.province } : {}),
              'postalCode': company.postalCode,
              'addressCountry': company.country,
            },
          }
        : {}),
    }
  }

  function websiteJsonLd() {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteOrigin()}/#website`,
      'url': siteOrigin(),
      'name': SITE_NAME,
      'publisher': { '@id': `${siteOrigin()}/#organization` },
      'inLanguage': unref(locale),
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': `${absoluteUrl(localePath('/search'))}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    }
  }

  function breadcrumbJsonLd(items: BreadcrumbItem[]) {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': items.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.label,
        // Breadcrumbs are always within the current locale, so the URL must
        // include the locale prefix to match the canonical of the linked page.
        ...(item.href ? { item: canonicalForPath(item.href) } : {}),
      })),
    }
  }

  function stripHtml(html: string | undefined | null): string {
    return (html ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }

  function variantAvailability(stockLevel: string | undefined | null): string {
    if (!stockLevel || stockLevel === 'OUT_OF_STOCK')
      return 'https://schema.org/OutOfStock'
    if (stockLevel === 'LOW_STOCK')
      return 'https://schema.org/LimitedAvailability'
    return 'https://schema.org/InStock'
  }

  function priceValidUntil(): string {
    // Google warns if `priceValidUntil` is missing or in the past. One year
    // forward is the common convention for evergreen catalogue pricing.
    const d = new Date()
    d.setFullYear(d.getFullYear() + 1)
    return d.toISOString().slice(0, 10)
  }

  /** Minor units to the decimal string every money value in this file uses. */
  function money(minor: number): string {
    return (minor / 100).toFixed(2)
  }

  /**
   * A campaign coupon (`?code=FB30`), if this visitor is carrying one. Read
   * here so the structured data can quote the same price the page does — see
   * `offerPrice`.
   */
  const { coupon } = useUrlCoupon()

  /**
   * What an `Offer` should say `price` is: the catalogue price, or the campaign
   * price when a coupon is applying itself to this visitor's order.
   *
   * An auto-applied percentage code discounts the whole order and prorates over
   * every line, so the shopper really is quoted this figure everywhere — the
   * tile, the PDP, the cart, the checkout. Leaving the catalogue price in the
   * JSON-LD made the machine-readable copy contradict every price on the page
   * it was embedded in.
   *
   * This does not change what search engines index. A crawler arrives with
   * neither the campaign URL nor its cookie, so it resolves no coupon and reads
   * the catalogue price; and a `?code=` URL canonicalises to the clean one.
   * Only the campaign visitor's own HTML is rewritten — which is also why that
   * render is never edge-cached (see `plugins/3.urlCoupon`).
   *
   * `product` is what the coupon is weighed against: it steps around gift cards,
   * which are sold at face value, so an offer for one keeps its catalogue price.
   */
  function offerPrice(minor: number, product?: CouponFacetValueSource | null): number {
    const percent = coupon.value?.percent
    if (!percent || couponExcludes(coupon.value, product))
      return minor
    return couponPrice(minor, percent)
  }

  /**
   * A GTIN if `raw` really is one, `undefined` otherwise. Vendure's `barcode`
   * custom field is unvalidated free text, and a malformed `gtin` is worse than
   * an absent one: Google reports it as a merchant-listing error, while an
   * omitted `gtin` is merely a missing recommendation. So the length has to be
   * one of the four GTIN forms and the mod-10 check digit has to agree.
   */
  function normalizeGtin(raw: string | null | undefined): string | undefined {
    const digits = (raw ?? '').replace(/\D/g, '')
    if (![8, 12, 13, 14].includes(digits.length))
      return undefined

    // Weights alternate 3, 1 walking left from the digit before the check digit.
    const body = digits.slice(0, -1)
    const sum = [...body]
      .reverse()
      .reduce((acc, digit, index) => acc + Number(digit) * (index % 2 === 0 ? 3 : 1), 0)

    return (10 - (sum % 10)) % 10 === Number(digits.slice(-1)) ? digits : undefined
  }

  /**
   * The variant's EAN, from the `barcode` custom field the app-level
   * `ProductVariant` fragment selects (see `nuxt.config.ts`). Null on the ~1% of
   * the catalogue that has none, in which case the offer carries no `gtin`.
   */
  function variantGtin(variant: ProductVariantFragment): string | undefined {
    return normalizeGtin(variant.customFields?.barcode)
  }

  /**
   * The style-level code shared by every size of this product: the longest
   * common prefix of the variant SKUs measured in whole dash-SEGMENTS, e.g.
   * `EXLTD-F-AURA-ZIPPERTEE-WHITE-{XS,S,M,…}` → `EXLTD-F-AURA-ZIPPERTEE-WHITE`.
   *
   * Segments, not characters. A character-wise prefix cuts through a token
   * whenever two sizes start with the same letter: the sock ranges `-3438` and
   * `-3842` share a `3`, which yields the malformed
   * `EXLTD-U-MOTION-SOCKS-MULTICOLOR-WHBLBE-3` (3 products in the catalogue),
   * and a product stocked only in `XXS`/`XS` would end at `…-WHITE-X`.
   *
   * Also deliberately NOT "always strip the last segment". The 16 one-size
   * accessories — caps, a visor, a scrunchie — end in the COLOUR, so stripping
   * would hand `EXLTD-F-HAT-ICONCAPCLASSIC` to the beige, navy, burgundy and
   * blush-pink caps alike: four products, one `sku`. With a single variant the
   * common prefix is simply that variant's whole SKU, which is what they want.
   *
   * Verified over the live catalogue (638 products / 3499 variants): every code
   * is a clean segment prefix of its variants, 638 distinct codes, no
   * collisions. Vendure's numeric collision suffix (`…-IMPACT-LEGGINGS-2-BLACK`)
   * survives, since it precedes the size.
   */
  function styleSku(variants: ProductVariantFragment[]): string | undefined {
    const skus = variants.map(v => v.sku).filter((s): s is string => Boolean(s))
    if (!skus.length)
      return undefined

    const parts = skus.map(sku => sku.split('-'))
    const shortest = Math.min(...parts.map(p => p.length))
    const common: string[] = []
    for (let i = 0; i < shortest; i++) {
      const segment = parts[0]![i]!
      if (!parts.every(p => p[i] === segment))
        break
      common.push(segment)
    }

    // Nothing in common is only reachable if the SKUs are unrelated; the first
    // one is still a truthful identifier for this product.
    return common.join('-') || skus[0]
  }

  /** One decimal, exactly as `ProductReviews` prints it (`toFixed(1)`). */
  function roundRating(value: number): number {
    return Math.round(value * 10) / 10
  }

  /** At least two letters or digits — enough to tell real text from `-` or `...`. */
  function hasWords(value: string | null | undefined): boolean {
    return ((value ?? '').match(/[\p{L}\p{N}]/gu)?.length ?? 0) >= 2
  }

  /**
   * How many individual reviews ride along with the aggregate. Enough to back
   * the rating up, few enough that a page with 154 of them doesn't put its
   * whole reviews section into every visitor's HTML.
   */
  const MAX_SEO_REVIEWS = 5

  const BUSINESS_DAYS = [
    'https://schema.org/Monday',
    'https://schema.org/Tuesday',
    'https://schema.org/Wednesday',
    'https://schema.org/Thursday',
    'https://schema.org/Friday',
  ]

  function shippingDeliveryTime(market: MarketShipping) {
    return {
      '@type': 'ShippingDeliveryTime',
      'handlingTime': {
        '@type': 'QuantitativeValue',
        'minValue': HANDLING_DAYS.min,
        'maxValue': HANDLING_DAYS.max,
        'unitCode': 'DAY',
      },
      'transitTime': {
        '@type': 'QuantitativeValue',
        'minValue': market.transitDays.min,
        'maxValue': market.transitDays.max,
        'unitCode': 'DAY',
      },
      // Every quoted duration is in working days. `cutoffTime` is deliberately
      // omitted even though the warehouse has one: it carries a UTC offset that
      // moves with DST, and this HTML is edge-cached for five minutes, so twice
      // a year it would serve an hour-wrong cutoff. Google requires neither.
      'businessDays': {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': BUSINESS_DAYS,
      },
    }
  }

  /**
   * `OfferShippingDetails` for every destination this locale serves, priced
   * from the courier configuration the checkout itself quotes (see
   * `~/constants/marketPolicy`). The free-shipping amount is the one the
   * storefront advertises (Dashboard → Free delivery threshold), so Google is
   * quoted the same figure the policy tiles show; a destination with none gets
   * its rate and no free tier.
   *
   * ONE entry per destination, whose `shippingRate` is the real standard rate.
   * The free tier rides along inside `hasShippingService.shippingConditions` —
   * "at an order value of X or more the rate is 0" — rather than as a second
   * entry quoting 0. A second entry would be read as an unconditional free
   * delivery offer and print "Free delivery" against a basket that has not
   * reached the threshold, which is the kind of mismatch that costs a merchant
   * its listings. Encoding the threshold in `shippingRate` instead (as
   * `ShippingRateSettings.freeShippingThreshold`) is rejected outright: the
   * Rich Results Test reports "invalid object type in field shippingRate".
   *
   * Markets whose currency differs from the offer's are skipped — a rate
   * quoted in a currency the offer is not in is worse than no rate at all.
   */
  function offerShippingDetails(currency: string | undefined, freeShipping: FreeShippingThresholds | undefined) {
    if (!currency)
      return []

    return marketCountriesForLocale(unref(locale))
      .map(country => MARKET_SHIPPING[country])
      .filter(market => market.currency === currency)
      .map(market => ({
        market,
        freeShippingMinor: freeShipping ? freeShippingThresholdFor(freeShipping, market.country) : null,
      }))
      .map(({ market, freeShippingMinor }) => ({
        '@type': 'OfferShippingDetails',
        // Stable identity: the terms are one shipping policy quoted on every
        // size of every product, not a fact about this offer. Google asks for
        // it, and it lets a consumer collapse the repeats.
        '@id': `${siteOrigin()}/#shipping-${market.country}`,
        'shippingDestination': { '@type': 'DefinedRegion', 'addressCountry': market.country },
        'shippingRate': { '@type': 'MonetaryAmount', 'value': money(market.baseRateMinor), 'currency': market.currency },
        ...(freeShippingMinor !== null && { hasShippingService: {
          '@type': 'ShippingService',
          // Same reason as the enclosing node's `@id`: one free-shipping tier
          // per destination, quoted identically on every offer. Without it the
          // Rich Results Test reports "missing field id (optional)" once per
          // offer, which is noise in the merchant-listings report.
          '@id': `${siteOrigin()}/#shipping-service-${market.country}`,
          'shippingConditions': {
            '@type': 'ShippingConditions',
            'orderValue': {
              '@type': 'MonetaryAmount',
              'minValue': freeShippingMinor / 100,
              'currency': market.currency,
            },
            'shippingRate': { '@type': 'MonetaryAmount', 'value': '0.00', 'currency': market.currency },
          },
        } }),
        'deliveryTime': shippingDeliveryTime(market),
        'shippingSettingsLink': canonicalForPath(market.shippingPage),
      }))
  }

  /**
   * The voluntary 30-day, seller-paid return programme, or nothing at all for a
   * locale that has no market behind it (`en`/US, whose own return page puts the
   * return carriage on the buyer). `merchantReturnLink` is fetched by Google and
   * has to back the claim, so the countries come from the policy itself rather
   * than from whatever the current page happens to ship to.
   */
  function merchantReturnPolicyJsonLd() {
    const policy = returnsPolicyForLocale(unref(locale))
    if (!policy)
      return undefined

    return {
      '@type': 'MerchantReturnPolicy',
      // One policy shared by every offer, so it carries one stable identity.
      '@id': `${siteOrigin()}/#returns-${policy.windowDays}day`,
      'applicableCountry': policy.countries,
      'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
      'merchantReturnDays': policy.windowDays,
      'returnMethod': 'https://schema.org/ReturnByMail',
      'returnFees': policy.free
        ? 'https://schema.org/FreeReturn'
        : 'https://schema.org/ReturnFeesCustomerResponsibility',
      'refundType': 'https://schema.org/FullRefund',
      'merchantReturnLink': canonicalForPath(policy.policyPage),
    }
  }

  /**
   * Product schema with `Offer`/`AggregateOffer`, `AggregateRating` and
   * `Brand`. Covers a single product (one color); siblings share the same
   * canonical/family structure so each color page emits its own schema.
   */
  function productJsonLd(
    product: ProductFragment,
    opts: {
      url: string
      gender?: 'm' | 'f'
      category?: string
      /** Shade name for this color page, e.g. `Mocha Mist`. */
      color?: string
      /** Fabric this design is made of, e.g. `NYVRA™`. */
      material?: string
      /** Every size on sale, already in wearing order — `['XXS', 'XS', 'S', …]`. */
      sizes?: string[]
      images?: string[]
      ratingStars?: number
      ratingCount?: number
      /**
       * The newest few reviews, as the reviews section renders them. Google
       * wants `review` alongside `aggregateRating`; an aggregate on its own is
       * a rating with nothing behind it.
       */
      reviews?: SeoReview[]
      /** The storefront's advertised free-shipping thresholds (`useFreeShippingThresholds`). */
      freeShipping?: FreeShippingThresholds
    },
  ) {
    const variants = product.variants ?? []
    const images = opts.images?.length
      ? opts.images.map(p => absoluteUrl(p))
      : (product.assets ?? [])
          .map(a => a.preview)
          .filter((p): p is string => Boolean(p))
          .map(p => absoluteUrl(p))

    const firstVariant = variants[0]
    const currency = firstVariant?.currency

    const canonicalProductUrl = canonicalForPath(opts.url)

    // Built once and shared by every size: they are the same parcel to the same
    // country under the same policy, and the blocks are identical byte-for-byte,
    // so repeating them costs nothing once the response is gzipped.
    const shippingDetails = offerShippingDetails(currency, opts.freeShipping)
    const returnPolicy = merchantReturnPolicyJsonLd()

    function variantOffer(v: ProductVariantFragment) {
      const gtin = variantGtin(v)
      return {
        '@type': 'Offer',
        'sku': v.sku,
        // The SKU doubles as the MPN: these garments are made for EXALTED and
        // carry no separate manufacturer number, so the SKU *is* how the maker
        // identifies the part. Google wants an identifier on every offer and
        // accepts `mpn` alongside `brand` as one.
        'mpn': v.sku,
        // Omitted rather than emitted empty where the variant has no EAN.
        ...(gtin ? { gtin } : {}),
        'price': money(offerPrice(v.price, product)),
        'priceCurrency': v.currency,
        'availability': variantAvailability(v.stockLevel),
        'url': canonicalProductUrl,
        'itemCondition': 'https://schema.org/NewCondition',
        'priceValidUntil': priceValidUntil(),
        ...(shippingDetails.length ? { shippingDetails } : {}),
        ...(returnPolicy ? { hasMerchantReturnPolicy: returnPolicy } : {}),
      }
    }

    // A flat list of Offers rather than an AggregateOffer wrapper. Google treats
    // AggregateOffer as product-snippet-only: the merchant-listing experiences
    // that shipping and return data feed — the ones that print "Free delivery"
    // and "30-day returns" under the result — read individual Offer objects and
    // ignore an aggregate. Losing `lowPrice`/`highPrice` costs nothing, since
    // Google derives the range from the offers either way.
    const offers = variants.length > 1
      ? variants.map(variantOffer)
      : firstVariant
        ? variantOffer(firstVariant)
        : undefined

    const description = stripHtml(product.description) || product.name

    const data: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${canonicalProductUrl}#product`,
      'name': product.name,
      'description': description.slice(0, 5000),
      // The STYLE code, not a size's. This node is the garment; the size-level
      // identifiers (sku/mpn/gtin) belong to the individual offers below, which
      // already carry them. Publishing `…-XS` here said the whole product was
      // the extra-small.
      'sku': styleSku(variants),
      'mpn': styleSku(variants),
      'image': images,
      'url': canonicalProductUrl,
      'brand': { '@type': 'Brand', 'name': SITE_NAME },
      'offers': offers,
    }

    // A GTIN identifies one sellable item, so it stays a per-offer fact — the
    // style has no single barcode. Left off the Product node deliberately.
    if (opts.category)
      data.category = opts.category
    // Each color of a design is a separate Product page; `color` is what tells
    // search engines the siblings are variants rather than duplicates.
    if (opts.color)
      data.color = opts.color
    if (opts.material)
      data.material = opts.material
    // schema.org puts `size` on the Product, never on an Offer, so the sizes go
    // out as one list rather than per-offer. Genuinely per-offer sizing needs a
    // ProductGroup/hasVariant restructure, which is a bigger change than this.
    if (opts.sizes?.length)
      data.size = opts.sizes
    if (opts.gender)
      data.audience = { '@type': 'PeopleAudience', 'suggestedGender': opts.gender === 'm' ? 'male' : 'female' }
    if (opts.ratingStars && opts.ratingCount) {
      data.aggregateRating = {
        '@type': 'AggregateRating',
        // `reviewRating` arrives as a raw mean — 4.901234567901235 — which is
        // a precision the data cannot support and which no page ever shows.
        // One decimal is what `ProductReviews` prints (`toFixed(1)`), so the
        // structured data and the page now say the same number.
        'ratingValue': roundRating(opts.ratingStars),
        'reviewCount': opts.ratingCount,
        'bestRating': 5,
        'worstRating': 1,
      }

      // Individual reviews behind the aggregate. Only ones with an author and a
      // rating are usable: Google requires both, and a review missing either is
      // dropped anyway. The body is trimmed — these are snippets, not the page.
      const reviews = (opts.reviews ?? [])
        .filter(r => r.authorName?.trim() && typeof r.rating === 'number')
        .slice(0, MAX_SEO_REVIEWS)
        .map((r) => {
          const published = r.createdAt ? String(r.createdAt).slice(0, 10) : undefined
          const body = stripHtml(r.body)
          return {
            '@type': 'Review',
            'author': { '@type': 'Person', 'name': r.authorName!.trim() },
            'reviewRating': {
              '@type': 'Rating',
              'ratingValue': roundRating(r.rating!),
              'bestRating': 5,
              'worstRating': 1,
            },
            ...(published ? { datePublished: published } : {}),
            // Most rows store `-` as the summary — a placeholder the reviews
            // section never prints. Only a title with actual words becomes
            // `name`; publishing "-" would put a dash in the search result.
            ...(hasWords(r.summary) ? { name: r.summary!.trim().slice(0, 200) } : {}),
            ...(body ? { reviewBody: body.slice(0, 1000) } : {}),
          }
        })

      if (reviews.length)
        data.review = reviews
    }

    return data
  }

  /**
   * Collection / category page schema. Wraps the visible product cards in an
   * `ItemList` so Google can index the listing as a category.
   */
  function collectionJsonLd(opts: {
    name: string
    description?: string
    url: string
    items: {
      name: string
      href: string
      image?: string
      price?: number
      currency?: string
      /**
       * Only read to keep a campaign coupon off the offers it does not
       * discount — a gift card is quoted at its face value. See `offerPrice`.
       */
      facetValueIds?: readonly (string | number)[] | null
    }[]
  }) {
    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': opts.name,
      'description': opts.description,
      'url': canonicalForPath(opts.url),
      'isPartOf': { '@id': `${siteOrigin()}/#website` },
      'mainEntity': {
        '@type': 'ItemList',
        'numberOfItems': opts.items.length,
        'itemListElement': opts.items.map((item, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'url': canonicalForPath(item.href),
          'name': item.name,
          ...(item.image
            ? {
                item: {
                  '@type': 'Product',
                  'name': item.name,
                  'image': absoluteUrl(item.image),
                  'url': canonicalForPath(item.href),
                  ...(item.price != null && item.currency
                    ? {
                        offers: {
                          '@type': 'Offer',
                          'price': money(offerPrice(item.price, item)),
                          'priceCurrency': item.currency,
                          'availability': 'https://schema.org/InStock',
                        },
                      }
                    : {}),
                },
              }
            : {}),
        })),
      },
    }
  }

  /**
   * `name` with its audience appended — "Leggings for Women", cs "Legíny pro
   * ženy" — for a page whose gender is carried only by its URL slug.
   *
   * Vendure names both the `women-shorts` and `men-shorts` collections "Shorts",
   * and 64 product names are shared by a men's and a women's product, so without
   * this those pages ship byte-identical titles and compete for the same query.
   *
   * The audience is appended as a PREPOSITIONAL PHRASE rather than the adjective
   * `collections.gender.*_prefix` holds, because an adjective has to agree with
   * whatever noun it lands on and a Vendure name is arbitrary: cs "Dámské" is
   * right for "Legíny", wrong for the neuter plural "Trička a tílka" and the
   * masculine singular "Dlouhý rukáv", and "Damen alle Oberteile" is not German.
   * "pro ženy" / "dla kobiet" / "für Damen" govern their own case, so they
   * cannot disagree with the name in front of them — which matters because the
   * next collection is named by a merchandiser, not by this file.
   *
   * Returns `name` untouched when the gender is unknown (the genuinely unisex
   * products — socks, caps, the gift card) or when the name already says it.
   */
  function audienceName(name: string, gender: 'f' | 'm' | undefined | null): string {
    // Admin-entered names carry stray inner whitespace ("All Tops  "), which
    // HTML collapses in an H1 but shows verbatim in a <title>.
    const trimmed = name.replace(/\s+/g, ' ').trim()
    if (!trimmed || !gender)
      return trimmed

    const audience = gender === 'f' ? 'women' : 'men'
    // Both spellings of "already said it": the locale's own gender word
    // (`women-sportbra` is named "Women Sport Bra") and the phrase this would
    // append, obtained by rendering the template with an empty name.
    const phrase = t(`seo.audience_${audience}`, { name: '' }).trim()
    if (saysWord(trimmed, t(`collections.gender.${audience}`)) || saysWord(trimmed, phrase))
      return trimmed

    return t(`seo.audience_${audience}`, { name: trimmed })
  }

  return {
    SITE_NAME,
    siteOrigin,
    absoluteUrl,
    canonicalForPath,
    audienceName,
    setSeoMeta,
    addJsonLd,
    organizationJsonLd,
    websiteJsonLd,
    breadcrumbJsonLd,
    productJsonLd,
    collectionJsonLd,
    stripHtml,
  }
}
